import { AsyncThunkConfigWithRejectValue, createAsyncThunk } from '@/redux/createAsyncThunk';
import { getAuthToken, getZipCodeGeoCoordinates } from '@/redux/modules/account/user/user.selectors';
import { getIsBot } from '@/redux/modules/analytics';
import { getDeployment } from '@/redux/modules/config';
import { getDisplayedPromotedItemIds } from '@/redux/modules/search/aggregateResults/searchAggregate.selectors';
import { getUserExcludedFacets } from '@/redux/modules/search/exclusions/searchExclusions.selectors';
import { clearPromotedSearchResults, searchPromoted } from '@/redux/modules/searchPromoted';
import { getSellerName } from '@/redux/modules/seller/seller.selectors';
import { GlobalState } from '@/redux/store';
import FacetTypes from '@/types/search/enums/facetTypes';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { type AugmentedSearchQuery, FacetOverrides, SearchPageName } from '@/types/search/searchQuery';
import { isClientSide } from '@/utils/amplitude/analytics/core';
import {
    PriceResultsMatch,
    QueryIntent,
    QueryIntentFromApi,
    SearchContext,
} from '@/utils/amplitude/analytics/core.types';
import {
    trackSearchCompletedAnalytics,
    TrackSearchCompletedSearchType,
} from '@/utils/amplitude/analytics/search/trackSearchCompleted.analytics';
import { trackSearchRefinedAnalytics } from '@/utils/amplitude/analytics/search/trackSearchRefined.analytics';
import { createAmplitudeSearchQueryParams } from '@/utils/amplitude/analytics/utils/createAmplitudeSearchQueryParams';
import { getAppliedRefinements } from '@/utils/amplitude/analytics/utils/getAppliedRefinements';
import { getFilteredFieldFromFilterAction } from '@/utils/amplitude/analytics/utils/getFilteredFieldFromFilterAction';
import { getFullUrl } from '@/utils/amplitude/analytics/utils/getFullUrl';
import { resolveSemanticBrowseDimensionValuesInSearchQuery } from '@/utils/browsePath/browsePath';
import { AmplitudePageName } from '@/utils/getPageNameFromPath';
import { parseQueryParams } from '@/utils/queryParams';

import { getClientIpAddress } from '../browser';
import { getSearchPageSize } from '../pagination';
import { getSearchFacets } from '../searchFacetAggregates.selectors';
import { fetchCategoryResults, fetchSearchResults } from './search.api';
import { getAugmentedSearchQuery, getSearchCompletedTrigger, getSpellCorrectionAnalytics } from './search.helpers';
import {
    getAggregationsOnDemandStatus,
    getCurrentSearch,
    getLastSearchFetchOptions,
    getSearchDominantCategory,
    getSearchedItemIds,
    getSearchSellerItemsSearch,
    getSearchSellerItemsSearchSellerId,
    getSearchSpellCorrection,
    getSearchSubmittedFromPage,
    getSearchTotalFound,
    getSearchTotalSoldItems,
    getSearchUiState,
    getUseGeoLocation,
} from './search.selectors';
import {
    type DidYouMeanMode,
    type DominantCategory,
    FetchCategoryResultsParams,
    FetchSearchResultsParams,
    isErrorResponse,
    LOAD_SEARCH_RESULTS_SUCCESS,
    LOAD_SEARCH_RESULTS_SUCCESS_ACTION,
    QueryModifications,
    SAVE_LAST_SEARCH_FETCH_OPTIONS,
    SearchResultPayload,
    type SpellCorrection,
    SubmitSearchPayload,
} from './search.types';

// Discriminator for `lastSearchFetchOptions`. The category variant carries
// `isCategorySearch: true` so aggs-on-demand can replay the right endpoint.
type CategorySearchLastOptions = FetchCategoryResultsParams & { isCategorySearch: true };

const isCategorySearchOptions = (
    options: FetchSearchResultsParams | CategorySearchLastOptions
): options is CategorySearchLastOptions => 'isCategorySearch' in options && options.isCategorySearch === true;

/**
 * didYouMean can swap the results for the same query, so the corrected response must not be served from cache
 * to a page that opted out of correction (e.g. "Search instead for ..."). A corrected response is stored under
 * the key the corrected URL's own request builds: its query with no didYouMean.
 */
const getSearchCacheKey = (searchQuery: AugmentedSearchQuery, didYouMean?: DidYouMeanMode): string =>
    JSON.stringify({ ...searchQuery, didYouMean });

export type SubmitSearchRejection = { deepPageRequiresAuth: true };

export const submitSearch = createAsyncThunk<
    SearchResultPayload | string,
    SubmitSearchPayload,
    AsyncThunkConfigWithRejectValue<SubmitSearchRejection>
>(
    'la/domain/search/submitSearch',
    async (
        {
            didYouMean,
            pageName,
            popularityScoreReRankingVersion,
            rankingVersionVariant,
            routerState = {},
            searchQuery,
            skipAggs,
        },
        { dispatch, getState, rejectWithValue }
    ) => {
        const state = getState();
        const deployment = getDeployment(state);
        const authToken = getAuthToken(state);
        const ipAddress = getClientIpAddress(state);
        const isBot = getIsBot(state);
        const currentSearch = getCurrentSearch(state);
        const submittedFromPage = getSearchSubmittedFromPage(state);
        const { isZeroResults } = getSearchUiState(state);

        // Annotate facets which have user-defined search exclusions
        const searchExclusions = getUserExcludedFacets(state);
        const searchQueryForRequest = resolveSemanticBrowseDimensionValuesInSearchQuery(
            searchQuery,
            getSearchFacets(state)
        );
        const augmentedSearchQuery = getAugmentedSearchQuery(searchQueryForRequest, searchExclusions);

        /*
         * Ask for SEO metadata on SEO search and collections pages -- but only when there is a
         * search term to key it on. search-party looks the metadata up BY the term, and rejects an
         * empty one by 404ing the whole request ("Not Found -  is not a valid seo search term"),
         * which takes the page's results down with it rather than just omitting the metadata.
         *
         * Every collection had a keyword until LAAC-2372, whose paintings page defines its results
         * with category_id alone, so this never fired before.
         *
         * LAAC-2380: a non-empty term is not sufficient either -- search-party 404s the request for
         * any term it does not recognise. buildSearchQuery sets `seoSearch: false` explicitly for a
         * collection whose config owns the SEO copy, and that decision wins over the rule here.
         */
        const searchQueryWithSeo = {
            ...augmentedSearchQuery,
            seoSearch:
                augmentedSearchQuery.seoSearch ??
                ((pageName === SearchPageName.SEO_SEARCH || pageName === SearchPageName.COLLECTIONS) &&
                    Boolean(augmentedSearchQuery.searchTerm)),
        };

        const searchForCacheCheck = getSearchCacheKey(searchQueryWithSeo, didYouMean);
        if (searchForCacheCheck === currentSearch && !isZeroResults) {
            const cachedSearchUiState = getSearchUiState(state);
            const cachedItemIds = getSearchedItemIds(state);
            trackSearchCompletedOrRefined({
                deployment,
                dominantCategory: getSearchDominantCategory(state),
                featuredLotIds: getDisplayedPromotedItemIds(state, cachedItemIds),
                itemScores: cachedSearchUiState.itemScores,
                lotIds: cachedItemIds,
                pageName,
                queryModifications: cachedSearchUiState.queryModifications,
                refinementApplied: getAppliedRefinements(getSearchFacets(state)),
                routerState,
                // No correctedSearchTerm: a corrected search is cached under the corrected keyword, so a
                // cache hit already searched for it.
                searchTerm: searchQuery.searchTerm,
                spellCorrection: getSearchSpellCorrection(state),
                state,
                submittedFromPage,
                totalFound: getSearchTotalFound(state),
                totalSoldItems: getSearchTotalSoldItems(state),
            });
            return {};
        }

        const useGeoLocation = getUseGeoLocation(state);
        const geoCoordinates = getZipCodeGeoCoordinates(state);

        // searchQueryWithSeo contains search query + seoSearch flag to request SEO metadata from API on an SEO search page, and on a legacy collections page that has not opted out
        const options = {
            authToken,
            deployment,
            didYouMean,
            geoCoordinates,
            ipAddress,
            isBot,
            popularityScoreReRankingVersion,
            rankingVersionVariant,
            searchQuery: searchQueryWithSeo,
            skipAggs,
            useGeoLocation,
        };

        // Save options so we can re-run without skipAggs later for on-demand facets.
        dispatch({
            payload: options,
            type: SAVE_LAST_SEARCH_FETCH_OPTIONS,
        });

        // main search api dispatch
        const promises: Promise<any>[] = [fetchSearchResults(options)];
        // add search for promoted items if making a catalog search AND sort is Most Relevant
        if (pageName !== 'catalog' && searchQueryForRequest.sort === SearchSortAndDirection.MostRelevant) {
            promises.push(
                dispatch(
                    searchPromoted({
                        pageName,
                        rankingVersionVariant,
                        searchQuery: searchQueryForRequest,
                    })
                )
            );
        } else if (pageName !== 'catalog' && searchQueryForRequest.sort !== SearchSortAndDirection.MostRelevant) {
            // Clear promoted search results when sort is not Most Relevant
            dispatch(clearPromotedSearchResults());
        }

        let results: any;
        try {
            [results] = await Promise.all(promises);
        } catch (err) {
            // fetchSearchResults rejects with the bare status (403) when search-party
            // gates a deep page behind auth -- see manuallyHandledErrorCodes in search.api.ts.
            if (err === 403) {
                return rejectWithValue({ deepPageRequiresAuth: true });
            }
            throw err;
        }

        if (isErrorResponse(results)) {
            throw new Error(results.payload);
        }

        results.payload.currentQueryKey = searchForCacheCheck;

        // The page swaps the URL to the corrected keyword without reloading, so store this response as
        // the search that URL describes. search-party ignores didYouMean on aggsOnly requests: an
        // on-demand facet replay of the typed term would facet the misspelling instead.
        const { spellCorrection } = results.payload as SearchResultPayload;
        const correctedSearchTerm = spellCorrection?.isCorrected ? spellCorrection.correctedQuery : undefined;
        if (correctedSearchTerm) {
            const correctedSearchQuery = { ...searchQueryWithSeo, searchTerm: correctedSearchTerm };
            results.payload.currentQueryKey = getSearchCacheKey(correctedSearchQuery);
            // Saved after the await, unlike the options above: skip it when a newer search has saved its own
            // options in the meantime, or facet replays would run this older query.
            if (getLastSearchFetchOptions(getState())?.searchQuery === searchQueryWithSeo) {
                dispatch({
                    payload: { ...options, didYouMean: undefined, searchQuery: correctedSearchQuery },
                    type: SAVE_LAST_SEARCH_FETCH_OPTIONS,
                });
            }
        }

        // TODO: once all the reducers that subscribe to LOAD_SEARCH_RESULTS_SUCCESS are updated to toolkit, this dispatch should be removed
        const successAction: LOAD_SEARCH_RESULTS_SUCCESS_ACTION = {
            error: false,
            meta: { actionTime: Date.now() },
            payload: skipAggs ? { ...results.payload, soldItemFacets: null } : results.payload,
            type: LOAD_SEARCH_RESULTS_SUCCESS,
        };

        dispatch(successAction);

        if (!isZeroResults) {
            // Featured lots come from a separate /search/v4/web?promoted=1 call (searchPromoted
            // thunk above) and get blended into the SRP grid. Track only the IDs that actually
            // survive the blender — server-side returns more promoted than the grid renders, so
            // matching the raw slice would overstate Featured impressions. itemIds is passed
            // explicitly because submitSearch.fulfilled (which writes state.search.itemIds) only
            // fires after this thunk returns.
            // Read facets straight from the search response payload — `state.search.facets` is
            // the prior search's tree (the success-action dispatch above doesn't update it; only
            // `submitSearch.fulfilled` does, which runs after this thunk returns).
            trackSearchCompletedOrRefined({
                deployment,
                dominantCategory: results.payload.dominantCategory,
                featuredLotIds: getDisplayedPromotedItemIds(getState(), results.payload.itemIDs),
                itemScores: results.payload.itemScores,
                lotIds: results.payload.itemIDs,
                pageName,
                queryModifications: results.payload.queryModifications,
                refinementApplied: getAppliedRefinements(results.payload.facets),
                routerState,
                correctedSearchTerm,
                searchTerm: searchQuery.searchTerm,
                spellCorrection,
                state,
                submittedFromPage,
                totalFound: results.payload.totalFound,
                totalSoldItems: results.payload.totalSold,
            });
        }

        // Null out soldItemFacets on skipAggs so addSearchResults doesn't clobber soldItemFacets
        // populated in parallel by fetchSearchAggregationsOnDemand.fulfilled. `facets` is preserved
        // so search-party's synthesized selected-chip facets (skipAggs responses) reach the UI.
        return skipAggs ? { ...results.payload, soldItemFacets: null } : results.payload;
    }
);

type TrackSearchCompletedParams = {
    /**
     * Set when search-party swapped the typed keyword for this one; the results are for it.
     */
    correctedSearchTerm?: string;
    deployment: ReturnType<typeof getDeployment>;
    dominantCategory?: DominantCategory;
    featuredLotIds: number[];
    itemScores?: Record<string, number>;
    lotIds: number[];
    pageName: SubmitSearchPayload['pageName'];
    queryModifications?: QueryModifications;
    refinementApplied: string[];
    routerState: SubmitSearchPayload['routerState'];
    searchTerm: string;
    spellCorrection?: SpellCorrection;
    state: GlobalState;
    submittedFromPage: AmplitudePageName;
    totalFound: number;
    totalSoldItems: number;
};

const trackSearchCompletedOrRefined = ({
    correctedSearchTerm,
    deployment,
    dominantCategory,
    featuredLotIds,
    itemScores,
    lotIds,
    pageName,
    queryModifications,
    refinementApplied,
    routerState,
    searchTerm,
    spellCorrection,
    state,
    submittedFromPage,
    totalFound,
    totalSoldItems,
}: TrackSearchCompletedParams) => {
    if (isClientSide()) {
        const pageSize = getSearchPageSize(state);
        const sanitizedAnalyticsQueryParams = createAmplitudeSearchQueryParams(
            window?.location?.search ?? '',
            pageSize
        );

        const analyticsLotIds = lotIds.join(',');
        const reportedSearchTerm = correctedSearchTerm ?? searchTerm;
        const serializedItemScores =
            itemScores && Object.keys(itemScores).length > 0 ? JSON.stringify(itemScores) : undefined;

        // Facet applied (and not a related-search suggestion click). Tracking Search Refined
        if (routerState.filterAction && !routerState.suggestionClicked) {
            const filteredField = getFilteredFieldFromFilterAction(routerState.filterAction);
            const filteredFields = filteredField ? [filteredField] : [];
            // Drop URL-derived `categoryId` (raw taxonomy IDs) in favor of `refinementApplied`,
            // resolved "<FacetKey>:<OptionLabel>" strings sourced from the facet tree.
            const { categoryId: _droppedCategoryId, ...refinedAnalyticsQueryParams } = sanitizedAnalyticsQueryParams;
            const trackSearchRefinedPayload = {
                filteredFields,
                itemScores: serializedItemScores,
                lotIds: analyticsLotIds,
                normalizedSearchQuery: reportedSearchTerm?.toLowerCase() ?? '',
                numberOfSearchResults: totalFound,
                pageName: submittedFromPage,
                refinementApplied,
                searchQuery: reportedSearchTerm,
                url: getFullUrl(deployment, window.location.pathname),
                ...refinedAnalyticsQueryParams,
            };
            trackSearchRefinedAnalytics(trackSearchRefinedPayload);
        } else if (pageName !== SearchPageName.COLLECTIONS) {
            // LAAC-2603: arriving on a collection page is browsing, not a search.
            // No facet applied. Tracking Search Completed
            const isSellerItemsSearch = getSearchSellerItemsSearch(state);
            const sellerId = getSearchSellerItemsSearchSellerId(state);
            const sellerName = getSellerName(state, sellerId);

            const fromSellerPage = isSellerItemsSearch && !submittedFromPage && !searchTerm;

            const queryParams = parseQueryParams(window?.location?.search ?? '');

            // The search bar shows what the person typed, not the correction: the typed keyword until
            // the URL is swapped, then the URL's originalKeyword (e.g. going Back to a corrected search).
            const searchInput = document.getElementById('search-input') as HTMLInputElement;
            const submittedFromSearchBar = searchInput?.value === (queryParams.originalKeyword || searchTerm);

            // This gets added in useSearchProps.ts
            const searchSuggestionUsed = Boolean(routerState?.suggestionClicked);

            // Determine search context based on URL parameters
            const { pathname } = window.location;
            const isArchivedSearch = queryParams.status === 'archive' || pathname.includes('/price-guide');
            const searchContext = isArchivedSearch ? SearchContext.PriceResults : SearchContext.UpcomingItems;

            let queryIntent: QueryIntent | undefined = QueryIntent.None;
            if (queryModifications?.queryRewrite?.applied) {
                const queryIntentApiValue = queryModifications?.queryRewrite?.queryIntent;
                queryIntent = queryIntentApiValue
                    ? (QueryIntentFromApi[queryIntentApiValue] ?? QueryIntent.None)
                    : QueryIntent.None;
            }

            let modifiedQuery: string | undefined;
            if (queryIntent !== QueryIntent.None) {
                const modifiedQueryValue = queryModifications?.queryRewrite?.modifiedQuery;
                modifiedQuery = modifiedQueryValue === '' ? '(empty)' : modifiedQueryValue;
            }

            const trackSearchCompletedPayload = {
                featuredLotIds: featuredLotIds.join(','),
                itemScores: serializedItemScores,
                lotIds: analyticsLotIds,
                mainL1Category: dominantCategory?.l1 ?? null,
                mainL2Category: dominantCategory?.l2 ?? null,
                modifiedQuery,
                normalizedSearchQuery: (fromSellerPage ? sellerName : reportedSearchTerm)?.toLowerCase() ?? '',
                numberOfSearchResults: totalFound,
                page: sanitizedAnalyticsQueryParams.page,
                pageName: fromSellerPage ? AmplitudePageName.Auctioneer : submittedFromPage,
                pageSize,
                priceResultsMatch: totalSoldItems > 0 ? PriceResultsMatch.Yes : PriceResultsMatch.No,
                queryIntent,
                searchContext,
                searchQuery: fromSellerPage ? sellerName : reportedSearchTerm,
                searchSuggestionUsed,
                searchType: fromSellerPage
                    ? TrackSearchCompletedSearchType.Auctioneer
                    : TrackSearchCompletedSearchType.Keyword,
                ...getSpellCorrectionAnalytics({
                    originalKeyword: queryParams.originalKeyword,
                    spellCorrection,
                    trigger: sanitizedAnalyticsQueryParams.trigger,
                }),
                trigger: getSearchCompletedTrigger(
                    sanitizedAnalyticsQueryParams.trigger,
                    searchSuggestionUsed,
                    submittedFromSearchBar
                ),
                url: getFullUrl(deployment, window.location.pathname),
            };

            trackSearchCompletedAnalytics(trackSearchCompletedPayload);
        }
    }
};

export const updateSearchSubmittedFromPage = createAsyncThunk<AmplitudePageName, AmplitudePageName>(
    'la/domain/search/updateSearchSubmittedFromPage',
    (pageName) => pageName
);

export const updateSearchSellerItemsSearch = createAsyncThunk<boolean, boolean>(
    'la/domain/search/updateSearchSellerItemsSearch',
    (sellerItemsSearch) => sellerItemsSearch
);

export const updateSearchSellerItemsSearchSellerId = createAsyncThunk<number, number>(
    'la/domain/search/updateSearchSellerItemsSearchSellerId',
    (sellerItemsSearch) => sellerItemsSearch
);

export const fetchMoreSearchResults = createAsyncThunk<SearchResultPayload | string, SubmitSearchPayload>(
    'la/domain/search/fetchMoreSearchResults',
    async ({ pageName, searchQuery, skipAggs }, { dispatch, getState }) => {
        const state = getState();
        const deployment = getDeployment(state);
        const authToken = getAuthToken(state);
        const ipAddress = getClientIpAddress(state);
        const isBot = getIsBot(state);

        // Annotate facets which have user-defined search exclusions
        const searchExclusions = getUserExcludedFacets(state);
        const searchQueryForRequest = resolveSemanticBrowseDimensionValuesInSearchQuery(
            searchQuery,
            getSearchFacets(state)
        );
        const augmentedSearchQuery = getAugmentedSearchQuery(
            { ...(isBot ? { isBot } : {}), ...searchQueryForRequest },
            searchExclusions
        );

        const useGeoLocation = getUseGeoLocation(state);
        const geoCoordinates = getZipCodeGeoCoordinates(state);

        const promises: Promise<any>[] = [
            fetchSearchResults({
                authToken,
                deployment,
                geoCoordinates,
                ipAddress,
                searchQuery: augmentedSearchQuery,
                skipAggs,
                useGeoLocation,
            }),
        ];

        if (pageName !== 'catalog' && searchQueryForRequest.sort === SearchSortAndDirection.MostRelevant) {
            promises.push(dispatch(searchPromoted({ pageName, searchQuery: searchQueryForRequest })));
        } else if (pageName !== 'catalog' && searchQueryForRequest.sort !== SearchSortAndDirection.MostRelevant) {
            // Clear promoted search results when sort is not Most Relevant
            dispatch(clearPromotedSearchResults());
        }

        const [results] = await Promise.all(promises);

        if (isErrorResponse(results)) {
            throw new Error(results.payload);
        }

        // Defensive: no caller passes skipAggs=true to pagination today, but mirror the
        // null-out so a future opt-in can't reopen the addSearchResults race. `facets` is
        // preserved so synthesized selected-chip facets from search-party reach the UI.
        return skipAggs ? { ...results.payload, soldItemFacets: null } : results.payload;
    }
);

/**
 * Re-runs the last search WITHOUT skipAggs to fetch search-scoped facets on demand.
 * Only updates facets in state — does NOT update items, loading, or other search state.
 * Skips the fetch if facets are already loaded or currently loading.
 */
export const fetchSearchAggregationsOnDemand = createAsyncThunk<SearchResultPayload | null, void>(
    'la/domain/search/fetchSearchAggregationsOnDemand',
    async (_, { dispatch, getState }) => {
        const state = getState();
        const lastOptions = state.search.lastSearchFetchOptions;
        if (!lastOptions) {
            return null;
        }

        const results = isCategorySearchOptions(lastOptions)
            ? await fetchCategoryResults({
                  ...lastOptions,
                  aggsOnly: true,
                  skipAggs: undefined,
              })
            : await fetchSearchResults({
                  ...lastOptions,
                  aggsOnly: true,
                  skipAggs: undefined,
              });

        if (isErrorResponse(results)) {
            throw new Error('Failed to fetch search aggregations');
        }

        // Dispatch legacy action so other reducers pick up search results
        const successAction: LOAD_SEARCH_RESULTS_SUCCESS_ACTION = {
            error: false,
            meta: { actionTime: Date.now() },
            payload: results.payload,
            type: LOAD_SEARCH_RESULTS_SUCCESS,
        };
        dispatch(successAction);

        return results.payload;
    },
    {
        condition: (_, { getState }) => {
            const state = getState();
            const aggregationsOnDemandStatus = getAggregationsOnDemandStatus(state);
            const lastSearchFetchOptions = getLastSearchFetchOptions(state);

            // Proceed only when the last search skipped aggs and we haven't already
            // loaded (or started loading) the on-demand aggregation result.
            const aggregationsAlreadyInFlightOrLoaded =
                aggregationsOnDemandStatus === 'loading' || aggregationsOnDemandStatus === 'loaded';
            const lastSearchSkippedAggs = Boolean(lastSearchFetchOptions?.skipAggs);

            return !aggregationsAlreadyInFlightOrLoaded && lastSearchSkippedAggs;
        },
    }
);

/**
 * Re-runs the last search with `facetOverrides` merged in. Shared body for
 * `fetchFacetViewMore` and `fetchAuctionHouseTypeahead` -- both ask search-party
 * for the same query context but with a per-facet override (Size for View More,
 * SearchString for typeahead). The response replaces `state.facets` via the
 * shared `fulfilled` handler.
 *
 * Returns null when there's no last search to replay (cold load -- callers
 * should defer until the first `submitSearch` succeeds).
 *
 * **Why no LOAD_SEARCH_RESULTS_SUCCESS dispatch.** `fetchSearchAggregationsOnDemand`
 * fires the legacy action so analytics / item reducers can pick up the
 * response. Typeahead and View More refetches fire on a different cadence
 * (debounced keystrokes, repeated toggles) and the legacy dispatch's
 * downstream re-renders cascade up through AllFilters and remount the
 * AuctionHouseFilter -- which wipes the typeahead input's local state
 * mid-keystroke. Caught in stage smoke 2026-05-20. The per-thunk
 * `.fulfilled` handler in search.reducer.ts updates `state.facets`
 * directly, which is the only state change typeahead / View More need.
 *
 * **Race semantics: last-to-arrive wins.** If the user fires multiple
 * overlapping dispatches (e.g. quick typing followed by a View More click),
 * the response that arrives last replaces `state.facets`. The browser
 * order is debounced + sequential per network, so in practice the race
 * is bounded -- but a slow earlier typeahead response could land after a
 * faster View More response and shrink the facet back down. Acceptable
 * for v1; revisit with an AbortController / sequence-number guard if
 * QA surfaces concrete user-visible artifacts.
 */
const refetchWithFacetOverrides = async (
    lastOptions: NonNullable<ReturnType<typeof getLastSearchFetchOptions>>,
    facetOverrides: FacetOverrides
): Promise<SearchResultPayload> => {
    const augmentedSearchQuery = {
        ...lastOptions.searchQuery,
        facetOverrides: { ...(lastOptions.searchQuery.facetOverrides ?? {}), ...facetOverrides },
    };
    // Re-narrow inside each branch -- TS doesn't preserve the discriminated
    // narrowing through a pre-built spread object.
    const results = isCategorySearchOptions(lastOptions)
        ? await fetchCategoryResults({
              ...lastOptions,
              aggsOnly: true,
              searchQuery: augmentedSearchQuery,
              skipAggs: undefined,
          })
        : await fetchSearchResults({
              ...lastOptions,
              aggsOnly: true,
              searchQuery: augmentedSearchQuery,
              skipAggs: undefined,
          });

    if (isErrorResponse(results)) {
        throw new Error('Failed to refetch with facet overrides');
    }

    return results.payload;
};

type FetchFacetViewMoreArg = {
    facetId: string;
    size: number;
};

/**
 * "View More" expansion: re-runs the last search with a larger Size cap on
 * one facet so the drawer can render the full bucket list on demand.
 * search-party silently clamps over-cap values per facet.
 */
export const fetchFacetViewMore = createAsyncThunk<SearchResultPayload | null, FetchFacetViewMoreArg>(
    'la/domain/search/fetchFacetViewMore',
    async ({ facetId, size }, { getState }) => {
        const lastOptions = getLastSearchFetchOptions(getState());
        if (!lastOptions) {
            return null;
        }
        return refetchWithFacetOverrides(lastOptions, { [facetId]: { size } });
    }
);

type FetchAuctionHouseTypeaheadArg = {
    searchString: string;
};

/**
 * AuctionHouse search-as-you-type: re-runs the last search with a typed
 * value on the auctionHouse override so search-party emits an additional
 * `auctionHouseName` facet whose options are name-resolved House.IDs.
 * The hook layer debounces input; this thunk fires unconditionally per call.
 */
export const fetchAuctionHouseTypeahead = createAsyncThunk<SearchResultPayload | null, FetchAuctionHouseTypeaheadArg>(
    'la/domain/search/fetchAuctionHouseTypeahead',
    async ({ searchString }, { getState }) => {
        const lastOptions = getLastSearchFetchOptions(getState());
        if (!lastOptions) {
            return null;
        }
        return refetchWithFacetOverrides(lastOptions, { [FacetTypes.AUCTION_HOUSE_ID]: { searchString } });
    }
);

export const setUseGeoLocation = createAsyncThunk<boolean, boolean>(
    'la/domain/search/setUseGeoLocation',
    async (useGeoLocation) => useGeoLocation
);

export const setIsZeroResults = createAsyncThunk<boolean, boolean>(
    'la/domain/search/setIsZeroResults',
    async (isZeroResults) => isZeroResults
);
